// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { useMergeRefs, useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { getBaseProps } from '../../base-component';
import { GroupedControlContext, } from '../../context/control-group-context';
import { useFitsInline } from '../../hooks/use-fits-inline';
import { flattenChildren } from '../../utils/flatten-children';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
const InternalControlGroup = forwardRef(({ children, direction = 'auto', inlineLabelText, ...props }, ref) => {
    const baseProps = getBaseProps(props);
    const labelId = useUniqueId('control-group-label');
    const flattenedChildren = flattenChildren(children, 'ControlGroup');
    const controlCount = flattenedChildren.length;
    // Only `'auto'` measures; a forced direction wins and the ghost is not rendered.
    const { overflows, rootRef, ghostRef } = useFitsInline();
    const mergedRootRef = useMergeRefs(ref, rootRef);
    const resolvedDirection = direction === 'auto' ? (overflows ? 'vertical' : 'horizontal') : direction;
    const renderControlSlots = () => flattenedChildren.map((child, index) => {
        const key = child && typeof child === 'object' ? child.key : undefined;
        const position = controlCount === 1 ? 'only' : index === 0 ? 'first' : index === controlCount - 1 ? 'last' : 'middle';
        return (React.createElement("div", { key: key ? String(key) : index, className: clsx(testUtilStyles.control, styles.control, styles[`control-${position}-${resolvedDirection}`]) },
            React.createElement(GroupedControlContext.Provider, { value: { position, direction: resolvedDirection } }, child)));
    });
    // The role="group" subtree is identical whether or not a label is present: it keeps our root
    // ref, test class, structural classes, and the measurement ghost (only in `auto` mode). We
    // build it once and place it either bare (spreading baseProps) or inside the inline-label
    // wrapper (where baseProps live on the outer wrapper instead). `extraProps` carries whatever
    // the chosen branch puts on the group div (baseProps when bare, aria-labelledby when labeled)
    // and `className` is any extra class to merge with the structural ones.
    const renderGroup = (extraProps, className) => (React.createElement("div", { ...extraProps, ref: mergedRootRef, role: "group", className: clsx(className, testUtilStyles.root, styles.root, styles[`root-${resolvedDirection}`]) },
        renderControlSlots(),
        direction === 'auto' && (React.createElement("div", { ref: ghostRef, className: styles.ghost, "aria-hidden": "true" }, renderControlSlots()))));
    if (inlineLabelText) {
        // When labeled, the consumer's props/className land on the outer wrapper (matching main);
        // the inner role="group" div keeps the structural classes, ref, and test class.
        return (React.createElement("div", { ...baseProps, className: clsx(baseProps.className, testUtilStyles['inline-label-wrapper'], styles['inline-label-wrapper']) },
            React.createElement("label", { id: labelId, className: clsx(testUtilStyles['inline-label'], styles['inline-label']) }, inlineLabelText),
            React.createElement("div", { className: styles['inline-label-trigger-wrapper'] }, renderGroup({ 'aria-labelledby': labelId }))));
    }
    // When unlabeled, the consumer's props/className land directly on the role="group" root.
    return renderGroup(baseProps, baseProps.className);
});
export default InternalControlGroup;
//# sourceMappingURL=index.js.map